
export default (activityId, actVon, actBis, isActivityInProgress) => ({
    async init() {
        this.syncDateRanges({
            checkboxId: '#syncEditDates',
            sourceFromId: '#actPortalViewVon',
            sourceToId: '#actPortalViewBis',
            targetFromId: '#actPortalEditVon',
            targetToId: '#actPortalEditBis'
        });
    },


    /**
     * Synchronizes two date range pairs (source -> target) using a checkbox
     *
     * @param {Object} config - Configuration object
     * @param {string} config.checkboxId - ID of the synchronization checkbox
     * @param {string} config.sourceFromId - ID of the source "From" date field
     * @param {string} config.sourceToId - ID of the source "To" date field
     * @param {string} config.targetFromId - ID of the target "From" date field
     * @param {string} config.targetToId - ID of the target "To" date field
     * @param {number} [config.delay=100] - Delay before initialization (in ms),
     *                                     used to ensure Flatpickr is fully initialized
     */
    syncDateRanges(config) {
    const {
        checkboxId,
        sourceFromId,
        sourceToId,
        targetFromId,
        targetToId,
        delay = 100
    } = config;

    const el = this.$el;
    // Delay execution to ensure Flatpickr instances are available
    setTimeout(function () {
        // Retrieve DOM elements
        const syncCheckbox = el.querySelector(checkboxId);
        const sourceFromInput = el.querySelector(sourceFromId);
        const sourceToInput = el.querySelector(sourceToId);
        const targetFromInput = el.querySelector(targetFromId);
        const targetToInput = el.querySelector(targetToId);

        // Abort if any required element is missing
        if (!syncCheckbox || !sourceFromInput || !sourceToInput || !targetFromInput || !targetToInput) {
            return;
        }

        // Access Flatpickr instances
        const sourceFromPicker = sourceFromInput._flatpickr;
        const sourceToPicker = sourceToInput._flatpickr;
        const targetFromPicker = targetFromInput._flatpickr;
        const targetToPicker = targetToInput._flatpickr;

        // Target pickers are mandatory for synchronization
        if (!targetFromPicker || !targetToPicker) {
            return;
        }

        // Flatpickr visible inputs when altInput: true is enabled
        const targetFromAlt = targetFromPicker.altInput;
        const targetToAlt = targetToPicker.altInput;

        /**
         * Determines whether the synchronization checkbox
         * should be checked by default.
         *
         * Checked if:
         * - target fields are empty, OR
         * - target dates already match source dates
         */
        function shouldBeCheckedByDefault() {
            const targetFromEmpty = !targetFromInput.value || targetFromInput.value.trim() === '';
            const targetToEmpty = !targetToInput.value || targetToInput.value.trim() === '';
            const datesIdentical =
                targetFromInput.value === sourceFromInput.value &&
                targetToInput.value === sourceToInput.value;

            return (targetFromEmpty && targetToEmpty) || datesIdentical;
        }

        // Initialize checkbox state
        syncCheckbox.checked = shouldBeCheckedByDefault();

        /**
         * Copies source dates to target dates
         * (only when synchronization is enabled)
         */
        function syncDates() {
            if (syncCheckbox.checked) {
                copyValueOnceFlatpickr(sourceFromInput, targetFromInput);
                copyValueOnceFlatpickr(sourceToInput, targetToInput);
            }
        }

        /**
         * Enables or disables a Flatpickr altInput
         * - Applies readonly state
         * - Toggles a CSS class for visual styling
         * - Prevents or allows calendar opening
         */
        function setAltInputDisabled(altInput, picker, disabled) {
            if (!altInput || !picker) return;

            altInput.readOnly = disabled;
            altInput.classList.toggle('flatpickr-disabled', disabled);
            picker.set('clickOpens', !disabled);
        }

        /**
         * Updates readonly / disabled state of target date fields
         * based on the checkbox state
         */
        function updateReadonlyState() {
            const disabled = syncCheckbox.checked;
            setAltInputDisabled(targetFromAlt, targetFromPicker, disabled);
            setAltInputDisabled(targetToAlt, targetToPicker, disabled);
        }

        // Initial state setup
        updateReadonlyState();
        if (syncCheckbox.checked) {
            syncDates();
        }

        // Handle checkbox toggle
        syncCheckbox.addEventListener('change', function () {
            updateReadonlyState();
            if (this.checked) {
                syncDates();
            }
        });

        // Keep target dates in sync when source dates change
        if (sourceFromPicker) {
            sourceFromPicker.config.onChange.push(function () {
                if (syncCheckbox.checked) {
                    copyValueOnceFlatpickr(sourceFromInput, targetFromInput);
                }
            });
        }

        if (sourceToPicker) {
            sourceToPicker.config.onChange.push(function () {
                if (syncCheckbox.checked) {
                    copyValueOnceFlatpickr(sourceToInput, targetToInput);
                }
            });
        }
    }, delay);
}
});

function copyValueOnceFlatpickr(fromEl, toEl) {
    if (!fromEl || !toEl) return;

    const fromFp = fromEl._flatpickr;
    const toFp = toEl._flatpickr;

    if (fromFp && toFp) {
        const date = fromFp.selectedDates[0];
        if (date) {
            toFp.setDate(date, true);
        }
    }
}
